// The Brussels Airport website opens the booking page with the flight hour written in the
// traveller's browser timezone. Flights are stored in Brussels time, so the pre-fill must be
// converted before it reaches the form. Date and hour move together: the shift can cross midnight.
//
// The conversion assumes the deep link is opened in the browser that created it. A link forwarded
// to a traveller in another timezone is corrected a second time. This is accepted (COMPR-169).
// Delete this conversion when Brussels Airport sends the hour in Brussels time or adds a timezone
// parameter to the link. The deep-link contract with Brussels Airport is tracked on COMPR-169.
//
// Intl is used instead of moment-timezone (declared in package.json, imported nowhere) to keep the
// IANA database out of the bundle. The file is .mjs so that `node --test` loads it as an ES module
// without a package-level "type": "module". Vite resolves the extension-less import.

const BRUSSELS_TIME_ZONE = 'Europe/Brussels';
// Must equal App\Domains\Company\Enums\CustomerBrand::Bac.
const BRUSSELS_AIRPORT_BRAND = 'bac';
const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_PATTERN = /^(\d{2}):(\d{2})$/;

function getBrowserTimeZone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (error) {
        return undefined;
    }
}

function wallClockIn(instant, timeZone) {
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    }).formatToParts(instant);
    const value = (type) => parseInt(parts.find((part) => part.type === type).value, 10);

    return {
        year: value('year'),
        month: value('month'),
        day: value('day'),
        // Some engines print midnight as 24 even with hourCycle h23.
        hour: value('hour') % 24,
        minute: value('minute'),
    };
}

function asUtcMillis(wallClock) {
    return Date.UTC(wallClock.year, wallClock.month - 1, wallClock.day, wallClock.hour, wallClock.minute);
}

function offsetMillisAt(instant, timeZone) {
    return asUtcMillis(wallClockIn(instant, timeZone)) - instant.getTime();
}

// A wall-clock value in a timezone has no fixed offset around a DST change, so the offset is
// looked up twice: once at the naive UTC guess, once at the corrected instant.
function instantFromWallClock(wallClock, timeZone) {
    const naive = asUtcMillis(wallClock);
    const firstGuess = naive - offsetMillisAt(new Date(naive), timeZone);

    return new Date(naive - offsetMillisAt(new Date(firstGuess), timeZone));
}

// Shape checks alone let Date.UTC roll an impossible value into a plausible one: 24:00 becomes
// 01:00 the next day and month 99 lands years ahead. Reject anything that does not round-trip.
function isRealWallClock(wallClock) {
    if (wallClock.hour > 23 || wallClock.minute > 59 || wallClock.month < 1 || wallClock.month > 12 || wallClock.day < 1) {
        return false;
    }
    const probe = new Date(Date.UTC(wallClock.year, wallClock.month - 1, wallClock.day));

    return probe.getUTCFullYear() === wallClock.year
        && probe.getUTCMonth() === wallClock.month - 1
        && probe.getUTCDate() === wallClock.day;
}

function pad(number) {
    return String(number).padStart(2, '0');
}

// The booking page of the Brussels Airport website. Its flight hours are converted from the browser
// timezone (above) and its flight time labels carry the Brussels local time hint (COMPR-674).
function isBrusselsAirportBrand(customerBrand) {
    return customerBrand === BRUSSELS_AIRPORT_BRAND;
}

function convertDeepLinkedFlightTime({ customerBrand, date, time, timeZone }) {
    const unchanged = { date: date, time: time };

    if (!isBrusselsAirportBrand(customerBrand) || typeof timeZone !== 'string' || timeZone.length === 0) {
        return unchanged;
    }
    // A Brussels browser already shows Brussels time. Returning early also keeps a value on the
    // switch night (a local hour that does not exist) untouched instead of shifting it.
    if (timeZone === BRUSSELS_TIME_ZONE) {
        return unchanged;
    }

    const dateMatch = typeof date === 'string' ? date.match(DATE_PATTERN) : null;
    const timeMatch = typeof time === 'string' ? time.match(TIME_PATTERN) : null;
    if (dateMatch === null || timeMatch === null) {
        return unchanged;
    }

    const wallClock = {
        year: parseInt(dateMatch[1], 10),
        month: parseInt(dateMatch[2], 10),
        day: parseInt(dateMatch[3], 10),
        hour: parseInt(timeMatch[1], 10),
        minute: parseInt(timeMatch[2], 10),
    };
    if (!isRealWallClock(wallClock)) {
        return unchanged;
    }

    try {
        const instant = instantFromWallClock(wallClock, timeZone);
        const brussels = wallClockIn(instant, BRUSSELS_TIME_ZONE);

        return {
            date: `${brussels.year}-${pad(brussels.month)}-${pad(brussels.day)}`,
            time: `${pad(brussels.hour)}:${pad(brussels.minute)}`,
        };
    } catch (error) {
        return unchanged;
    }
}

export { convertDeepLinkedFlightTime, getBrowserTimeZone, isBrusselsAirportBrand };
